/**
 * "Create now" on a group template's item or subitem asks where it goes first:
 * an item picks the group to land in (the board's groups, the first by
 * default); a subitem picks the item to sit under (the board's items,
 * searched as the user types, the current item preselected in an item view).
 *
 *   { kind: 'group' | 'item', title, blurb, options: [{ id, title, color? }],
 *     loading, error, selectedId, onSearch(text), onConfirm(id), onClose }
 */
import { Button, Dropdown } from '@vibe/core';
import React, { useMemo, useState } from 'react';
import AnchoredPopover from './TemplateEditor/AnchoredPopover';

export default function CreateNowPopover({ anchorEl, kind, title, blurb, options, loading, error, selectedId, onSearch, onConfirm, onClose }) {
  const [picked, setPicked] = useState(selectedId || null);
  const list = useMemo(
    () => (options || []).map((o) => ({ value: String(o.id), label: o.title, startElement: o.color ? { type: 'indicator', color: o.color } : undefined })),
    [options],
  );
  const current = picked !== null ? picked : list.length ? list[0].value : null;
  const value = list.find((o) => o.value === String(current)) || null;
  const confirmLabel = kind === 'group' ? 'Create item' : 'Create subitem';
  return (
    <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={340}>
      <div className="spt-createnow-pop">
        <div className="spt-createnow-pop__title">{title}</div>
        <p className="spt-createnow-pop__blurb">{blurb}</p>
        <label className="spt-createnow-pop__label">{kind === 'group' ? 'Group' : 'Item'}</label>
        <Dropdown
          size="small"
          clearable={false}
          searchable={kind === 'item'}
          loading={loading}
          aria-label={kind === 'group' ? 'Group' : 'Item'}
          placeholder={kind === 'group' ? 'Choose a group' : 'Search items'}
          noOptionsMessage={loading ? 'Loading…' : kind === 'group' ? 'This board has no groups' : 'No item matches'}
          options={list}
          value={value}
          onInputChange={onSearch ? (text) => onSearch(text || '') : undefined}
          onChange={(o) => o && setPicked(o.value)}
        />
        {error && (
          <p className="spt-createnow-pop__error" role="alert">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-3">
          <Button kind="tertiary" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" disabled={!value} onClick={() => value && onConfirm(value.value)}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </AnchoredPopover>
  );
}
